/**
 * useSSE — React hook for streaming alert updates.
 *
 * Adam's APIs require Bearer auth, but the native EventSource API cannot attach
 * custom headers. We therefore consume the text/event-stream over fetch so the
 * same session token works for both API and live update traffic.
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000];
const AUTH_KEY = "agentme-adam-auth";

function getAuthToken(): string | null {
  try {
    const stored = sessionStorage.getItem(AUTH_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { token?: string };
    return typeof parsed.token === "string" ? parsed.token : null;
  } catch {
    return null;
  }
}

function handleEvent(queryClient: ReturnType<typeof useQueryClient>, eventName: string): void {
  if (eventName === "alert:new") {
    queryClient.invalidateQueries({ queryKey: ["alerts"] });
    queryClient.invalidateQueries({ queryKey: ["alerts-summary"] });
    queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
    return;
  }

  if (eventName === "alert:resolved") {
    queryClient.invalidateQueries({ queryKey: ["alerts"] });
    queryClient.invalidateQueries({ queryKey: ["alerts-summary"] });
    return;
  }

  if (eventName === "alert:summary") {
    queryClient.invalidateQueries({ queryKey: ["alerts-summary"] });
  }
}

export function useSSE(): void {
  const queryClient = useQueryClient();
  const retryCountRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function scheduleReconnect() {
      if (disposed) return;
      const delay = RECONNECT_DELAYS[Math.min(retryCountRef.current, RECONNECT_DELAYS.length - 1)];
      retryCountRef.current += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, delay);
    }

    async function connect() {
      abortRef.current?.abort();

      const token = getAuthToken();
      if (!token) {
        scheduleReconnect();
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetch("/api/events", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`sse_http_${response.status}`);
        }

        retryCountRef.current = 0;
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let currentEvent = "message";

        while (!disposed) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split(/\r?\n/);
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line) {
              handleEvent(queryClient, currentEvent);
              currentEvent = "message";
              continue;
            }

            if (line.startsWith(":")) {
              continue;
            }

            if (line.startsWith("event:")) {
              currentEvent = line.slice("event:".length).trim() || "message";
            }
          }
        }
      } catch (error) {
        if (disposed) return;
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
      } finally {
        if (!disposed) {
          scheduleReconnect();
        }
      }
    }

    void connect();

    return () => {
      disposed = true;
      abortRef.current?.abort();
      abortRef.current = null;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
    };
  }, [queryClient]);
}
